/**
 * The guest keyring on the device (part 38c, principles GP2, GP3; tables KG and CL; part 38a's KW): opened with the
 * password's wrapping key, made the first time a claim needs it, an entry sealed per claim, and wrapped again under a
 * new password. Every wrap made here is unwrapped again before it is sent (principle 7), and a record or entry that
 * doesn't open is refused, never used (KG4, KW7). WebCrypto and the randomness are injected.
 */
import { KEYRING_ID_BYTES, KEYRING_KEY_BYTES, openPersonKey, sealPersonKey, unwrapKeyring, wrapKeyring } from '@cockatrice/crypto';
import type { KdfRecord } from '@cockatrice/crypto';
import { fromBase64Url } from '../account/bytes.ts';
import type { HeldKeyring } from './store.ts';
import type { WireKeyring } from './wire.ts';

/** Random bytes into `bytes` (`crypto.getRandomValues`). */
export type Random = (bytes: Uint8Array<ArrayBuffer>) => Uint8Array;

/** GP3: what a typed password gives the keyring: its wrapping key and the KDF record it was derived under. */
export interface PasswordKeys {
  readonly wrapping: Uint8Array<ArrayBuffer>;
  readonly kdf: KdfRecord;
}

/** What opening the relay's keyring found. */
export type OpenedKeyring =
  | { readonly kind: 'none' }
  | { readonly kind: 'refused' }
  | { readonly kind: 'opened'; readonly keyring: HeldKeyring; readonly persons: ReadonlyMap<string, Uint8Array<ArrayBuffer>> };

function fresh(random: Random, length: number): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(length);
  random(bytes);
  return bytes;
}

/** A 12-byte IV. */
function iv(random: Random): Uint8Array<ArrayBuffer> {
  return fresh(random, 12);
}

/** An invite's ID as bytes, or null (GP5: the relay's word is read, never trusted). */
function inviteBytes(invite: string): Uint8Array<ArrayBuffer> | null {
  const bytes = fromBase64Url(invite);
  return bytes?.length === 16 ? bytes : null;
}

/**
 * KG1, KG2, KG4: `account`'s keyring as the relay holds it (`wire`), opened with `wrapping` under the record's own KDF
 * record (a wrap made under another password doesn't open: `refused`), and each entry opened under its invite (one that
 * doesn't open is left out: CC11). `none` when the account has no keyring yet.
 */
export async function openKeyring(subtle: SubtleCrypto, account: string, wrapping: Uint8Array<ArrayBuffer>, wire: WireKeyring): Promise<OpenedKeyring> {
  if (wire.record === null) return { kind: 'none' };
  const { id, kdf, wrapped } = wire.record;
  const key = await unwrapKeyring(subtle, wrapping, { account, id, kdf }, wrapped);
  if (key === null) return { kind: 'refused' };
  const keyring = { id, key };
  return { kind: 'opened', keyring, persons: await openEntries(subtle, account, keyring, wire.keys) };
}

/** KG1, CC11: each of `entries` opened with `keyring` under its invite; one that doesn't open is left out. */
export async function openEntries(subtle: SubtleCrypto, account: string, keyring: HeldKeyring, entries: WireKeyring['keys']): Promise<ReadonlyMap<string, Uint8Array<ArrayBuffer>>> {
  const persons = new Map<string, Uint8Array<ArrayBuffer>>();
  for (const entry of entries) {
    const invite = inviteBytes(entry.invite);
    const person = invite === null ? null : await openPersonKey(subtle, keyring.key, { account, keyring: keyring.id, invite }, entry.sealed);
    if (person !== null) persons.set(entry.invite, person);
  }
  return persons;
}

/** A wrap of `keyring` under `keys` for `account`, unwrapped again before it is given (principle 7); null if it doesn't. */
async function tested(subtle: SubtleCrypto, random: Random, account: string, keyring: HeldKeyring, keys: PasswordKeys): Promise<Uint8Array<ArrayBuffer> | null> {
  const place = { account, id: keyring.id, kdf: keys.kdf };
  const wrapped = await wrapKeyring(subtle, keys.wrapping, place, keyring.key, iv(random));
  const back = await unwrapKeyring(subtle, keys.wrapping, place, wrapped);
  return back?.every((byte, index) => byte === keyring.key[index]) === true ? wrapped : null;
}

/** KW1, CL1: a new keyring for `account`: a random ID and key, wrapped under `keys` and tested; null if the test failed. */
export async function newKeyring(subtle: SubtleCrypto, random: Random, account: string, keys: PasswordKeys): Promise<{ readonly keyring: HeldKeyring; readonly wrapped: Uint8Array<ArrayBuffer> } | null> {
  const keyring = { id: fresh(random, KEYRING_ID_BYTES), key: fresh(random, KEYRING_KEY_BYTES) };
  const wrapped = await tested(subtle, random, account, keyring, keys);
  return wrapped === null ? null : { keyring, wrapped };
}

/** KG9, KW2: `keyring` wrapped again for `account` under a new password's `keys`, tested; null if the test failed. */
export function rewrapKeyring(subtle: SubtleCrypto, random: Random, account: string, keyring: HeldKeyring, keys: PasswordKeys): Promise<Uint8Array<ArrayBuffer> | null> {
  return tested(subtle, random, account, keyring, keys);
}

/** CL1: person key `person` of invite `invite` (base64url) sealed into `account`'s keyring, opened again first; null if it doesn't. */
export async function sealEntry(subtle: SubtleCrypto, random: Random, account: string, keyring: HeldKeyring, invite: string, person: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer> | null> {
  const bytes = inviteBytes(invite);
  if (bytes === null) return null;
  const place = { account, keyring: keyring.id, invite: bytes };
  const sealed = await sealPersonKey(subtle, keyring.key, place, person, iv(random));
  const back = await openPersonKey(subtle, keyring.key, place, sealed);
  return back?.every((byte, index) => byte === person[index]) === true ? sealed : null;
}
